/* eslint-disable sonarjs/cognitive-complexity */
import { isEmpty, map, truncate } from 'lodash-es';
import { DeliveryLogType, MessageCategory } from '@dev/ee-api/ee-api-models';
import { useSelector } from 'react-redux';

import {
    DeliveryLogTypeTranslations,
    filterStatusesToDeliveryLogTypees,
    MessageCategoryTranslations,
} from '@/services/dictionaries/Logs';
import { getReportsState } from '@/store/reports/actions';

import { EventsFiltersTypeEnum } from './Interfaces';

const castedStatuses = {
    bounced: 'error',
};
const statusesWithMessageCategories: string[] = [DeliveryLogType.Bounced, DeliveryLogType.WaitingToRetry];
/**
 *
 * @returns object with event statuses and message categories for bounced(error) status
 *
 */

export const useEventsFilterOptions = (
    eventsFiltersType?: EventsFiltersTypeEnum
): Record<keyof typeof filterStatusesToDeliveryLogTypees, any>[] => {
    const { eventsCounts } = useSelector(getReportsState);
    const isInProgressScreen = eventsFiltersType === EventsFiltersTypeEnum.InProgress;

    const bounceCategories = (status: string) => {
        const logResultsList = [];

        const statusName = status?.toLowerCase();

        if (!statusesWithMessageCategories?.includes(status)) {
            return logResultsList;
        }

        map(MessageCategory, (result: string): void => {
            const properStatusName = castedStatuses?.[statusName] || statusName;
            const count = +(eventsCounts?.[properStatusName]?.[result?.toLowerCase()] || 0);

            let label = MessageCategoryTranslations[result];
            label = truncate(label, {
                length: 21,
            });

            if (count) {
                logResultsList.push({
                    value: `${status}_${result}` as any,
                    label: label as string,
                    count,
                    parent: status,
                });
            }
        });

        return logResultsList;
    };

    if (isInProgressScreen) {
        return [DeliveryLogType.ReadyToSend, DeliveryLogType.WaitingToRetry, DeliveryLogType.Sending]?.reduce(
            (acc, status: string): any => ({
                ...acc,
                [status]: { label: DeliveryLogTypeTranslations?.[status], value: status },
            }),
            []
        );
    }

    if (!eventsCounts) {
        return [];
    }

    const logsStatuses = Object.keys(filterStatusesToDeliveryLogTypees);

    return logsStatuses?.reduce((acc, status: string): any => {
        const statusNameToDisplay = filterStatusesToDeliveryLogTypees[status]?.[0];
        const count = +(eventsCounts?.total?.[statusNameToDisplay?.toLowerCase()] || 0);

        if (count) {
            const messageCategories = bounceCategories(status);

            if (statusesWithMessageCategories?.includes(status) && !isEmpty(messageCategories)) {
                return {
                    ...acc,
                    [DeliveryLogTypeTranslations?.[status]]: messageCategories,
                };
            }
            return {
                ...acc,
                [status]: { label: DeliveryLogTypeTranslations?.[status] as string, value: status as any, count },
            };
        }
        return acc;
    }, {});
};
